import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clientsApi, authApi } from '../lib/api';
import { useAuth } from './AuthContext';

const ClientContext = createContext(null);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('all');
  const [permissions, setPermissions] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [roleDisplayName, setRoleDisplayName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchClients = useCallback(async () => {
    try {
      const res = await clientsApi.getAll();
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await authApi.getPermissions();
      setPermissions(res.data.permissions || []);
      setRoleName(res.data.role || '');
      setRoleDisplayName(res.data.display_name || '');
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      Promise.all([fetchClients(), fetchPermissions()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchClients, fetchPermissions]);

  const hasPermission = useCallback((perm) => {
    return permissions.includes('*') || permissions.includes(perm);
  }, [permissions]);

  const selectedClient = clients.find(c => c.client_id === selectedClientId) || null;

  const value = {
    clients,
    selectedClientId,
    setSelectedClientId,
    selectedClient,
    permissions,
    roleName,
    roleDisplayName,
    hasPermission,
    loading,
    fetchClients,
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};

export default ClientContext;
